import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { buildAssumptionRows } from '@/lib/assumptions';
import {
  CURRENCIES, INDUSTRIES, COMPANY_SIZES,
  INDUSTRY_DURATION_FACTORS, MATURITY_DURATION_FACTORS,
  getEffectiveMaturity, interpolateFactor,
} from '@/lib/calculationEngine';

// ── Palette ───────────────────────────────────────────────────────────────────
const BLUE   = [59, 130, 246];
const DARK   = [15, 23, 42];
const GRAY   = [100, 116, 139];
const LIGHT  = [248, 250, 252];
const WHITE  = [255, 255, 255];
const BORDER = [226, 232, 240];

const PROVENANCE_KEYS = {
  USER_PROVIDED: 'trust.provenanceUserProvided',
  DEFAULT_ASSUMPTION: 'trust.provenanceDefaultAssumption',
  MODELLED: 'trust.provenanceModelled',
  ISO_DERIVED: 'trust.provenanceIsoDerived',
  ESTIMATED: 'trust.provenanceEstimated',
  NOT_VERIFIED: 'trust.provenanceNotVerified',
};

const PHASE_COLORS = [  [59, 130, 246],   // gap_analysis      – blue
  [139, 92, 246],   // risk_assessment   – purple
  [16, 185, 129],   // policy_development– green
  [249, 115, 22],   // implementation    – orange
  [245, 158, 11],   // internal_audit    – amber
  [239, 68, 68],    // certification_audit– red
];

// ── Helpers ───────────────────────────────────────────────────────────────────
const sym  = (cfg) => CURRENCIES.find(c => c.value === cfg?.currency)?.symbol || '€';
const ind  = (cfg) => INDUSTRIES.find(i => i.value === cfg?.industry)?.label || cfg?.industry || '-';
const size = (cfg) => COMPANY_SIZES.find(s => s.value === cfg?.companySize)?.label || cfg?.companySize || '-';
const fmt  = (val, s) => `${s}${(val || 0).toLocaleString()}`;

function addFooter(doc, pageNum) {
  const pageW = doc.internal.pageSize.getWidth();
  const footY = doc.internal.pageSize.getHeight() - 14;
  doc.setDrawColor(...BORDER);
  doc.line(20, footY - 4, pageW - 20, footY - 4);
  doc.setFontSize(7);
  doc.setFont('helvetica', 'italic');
  doc.setTextColor(...GRAY);
  doc.text(
    'This estimate is indicative, generated by ISMS Calculator (ismscalculator.com). Actual costs may vary.',
    20, footY
  );
  doc.text(`Page ${pageNum}`, pageW - 20, footY, { align: 'right' });
}

// ── Gantt chart drawn with jsPDF primitives ───────────────────────────────────
function drawGanttChart(doc, phases, totalWeeks, startX, startY, availW) {
  const LABEL_W  = 50;
  const CHART_W  = availW - LABEL_W;
  const CHART_X  = startX + LABEL_W;
  const HEADER_H = 8;
  const ROW_H    = 13;
  const BAR_H    = 7;
  const BAR_PAD  = (ROW_H - BAR_H) / 2;
  const months   = Math.ceil(totalWeeks / 4);

  // Month header background
  doc.setFillColor(...LIGHT);
  doc.rect(CHART_X, startY, CHART_W, HEADER_H, 'F');

  // Month labels + vertical grid lines
  for (let i = 0; i <= months; i++) {
    const x = CHART_X + (i / months) * CHART_W;
    doc.setDrawColor(...BORDER);
    doc.setLineWidth(0.2);
    doc.line(x, startY, x, startY + HEADER_H + phases.length * ROW_H);
    if (i < months) {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(6);
      doc.setTextColor(...GRAY);
      doc.text(`M${i + 1}`, x + 1.5, startY + HEADER_H - 1.5);
    }
  }

  // Phase rows
  let cumWeek = 0;
  phases.forEach((phase, idx) => {
    const rowY    = startY + HEADER_H + idx * ROW_H;
    const startPct = cumWeek / totalWeeks;
    const widthPct = phase.durationWeeks / totalWeeks;
    cumWeek += phase.durationWeeks;

    const barX = CHART_X + startPct * CHART_W;
    const barW = Math.max(widthPct * CHART_W, 2.5);
    const [r, g, b] = PHASE_COLORS[idx % PHASE_COLORS.length];

    // Alternating row bg
    if (idx % 2 === 0) {
      doc.setFillColor(252, 253, 254);
      doc.rect(startX, rowY, availW, ROW_H, 'F');
    }

    // Horizontal row divider
    doc.setDrawColor(...BORDER);
    doc.setLineWidth(0.15);
    doc.line(startX, rowY + ROW_H, startX + availW, rowY + ROW_H);

    // Phase name
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7.5);
    doc.setTextColor(...DARK);
    const labelText = phase.name || phase.id;
    doc.text(labelText, startX + 2, rowY + BAR_PAD + 4.5);

    // Effort/duration sub-label
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(6);
    doc.setTextColor(...GRAY);
    doc.text(`${phase.effort}d / ${phase.durationWeeks}w`, startX + 2, rowY + BAR_PAD + 9.5);

    // Colored bar
    doc.setFillColor(r, g, b);
    doc.roundedRect(barX, rowY + BAR_PAD, barW, BAR_H, 1, 1, 'F');

    // Week label inside bar
    if (barW > 9) {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(6);
      doc.setTextColor(...WHITE);
      doc.text(`${phase.durationWeeks}w`, barX + 2, rowY + BAR_PAD + BAR_H / 2 + 1.5);
    }
  });

  // Outer border
  doc.setDrawColor(...BORDER);
  doc.setLineWidth(0.3);
  doc.rect(CHART_X, startY, CHART_W, HEADER_H + phases.length * ROW_H, 'S');

  return startY + HEADER_H + phases.length * ROW_H;
}

// ── Main export function ──────────────────────────────────────────────────────
export function generateEstimatePDF({ name, config, results, createdAt, tco, t }) {
  const doc   = new jsPDF('p', 'mm', 'a4');
  const pageW = doc.internal.pageSize.getWidth();
  const s     = sym(config);
  let y       = 0;

  // Canonical assumption rows — same data as the results panel and shared page.
  const assumptionRows = t
    ? buildAssumptionRows(results.assumptions || [], { t, formatCost: (v) => fmt(v, s) })
    : [];
  const hasTco = Boolean(t && tco?.years?.length);
  const totalPages = 2 + (assumptionRows.length > 0 ? 1 : 0) + (hasTco ? 1 : 0);

  // ── PAGE 1 ── Header ──────────────────────────────────────────────────────
  doc.setFillColor(...DARK);
  doc.rect(0, 0, pageW, 42, 'F');
  doc.setFillColor(...BLUE);
  doc.rect(0, 42, pageW, 3, 'F');

  doc.setTextColor(...WHITE);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(20);
  doc.text('ISMS Calculator', 20, 20);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(148, 163, 184);
  doc.text('ISO 27001 Implementation Cost & Effort Estimate', 20, 28);

  const dateStr = createdAt
    ? new Date(createdAt).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
    : new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  doc.setFontSize(8);
  doc.setTextColor(148, 163, 184);
  doc.text(`Generated: ${dateStr}`, pageW - 20, 20, { align: 'right' });
  doc.text(`Page 1 of ${totalPages}`, pageW - 20, 28, { align: 'right' });

  // Estimate name
  y = 55;
  doc.setTextColor(...DARK);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.text(name || 'Untitled Estimate', 20, y);
  y += 10;

  // Company profile strip
  doc.setFillColor(...LIGHT);
  doc.roundedRect(20, y, pageW - 40, 28, 3, 3, 'F');

  const profiles = [
    ['Industry',      ind(config)],
    ['Company Size',  size(config)],
    ['Locations',     String(config?.locations || 1)],
    ['Approach',      (config?.approach || 'mixed').charAt(0).toUpperCase() + (config?.approach || 'mixed').slice(1)],
    ['Currency',      config?.currency || 'EUR'],
    ['Maturity',      `Level ${config?.overallMaturity || 3}`],
  ];
  const colW = (pageW - 50) / profiles.length;
  profiles.forEach(([label, val], i) => {
    const x = 26 + i * colW;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.setTextColor(...GRAY);
    doc.text(label, x, y + 12);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8.5);
    doc.setTextColor(...DARK);
    doc.text(val, x, y + 19);
  });
  y += 36;

  // ── Key Metrics ───────────────────────────────────────────────────────────
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(...DARK);
  doc.text('Key Metrics', 20, y);
  y += 7;

  // Duration factor badges (industry + maturity)
  const iDurFactor = INDUSTRY_DURATION_FACTORS[config?.industry] || 1.0;
  const mLevel     = Math.round(getEffectiveMaturity(config || {}) * 10) / 10;
  const mDurFactor = interpolateFactor(MATURITY_DURATION_FACTORS, mLevel);
  const durationFactors = [];
  if (iDurFactor > 1.0) {
    const iLabel = INDUSTRIES.find(i => i.value === config?.industry)?.label?.split(' ')[0] || '';
    durationFactors.push(`${iLabel} +${Math.round((iDurFactor - 1) * 100)}%`);
  }
  const mDurPct = Math.round((mDurFactor - 1) * 100);
  if (mDurPct !== 0) {
    const sign = mDurPct > 0 ? '+' : '';
    durationFactors.push(`Maturity L${mLevel} ${sign}${mDurPct}%`);
  }
  const durationNote = durationFactors.length ? `(${durationFactors.join(', ')})` : '';

  const metrics = [
    { label: 'Total Effort',       value: `${results.totalEffort} person-days`,  color: BLUE,          sub: `${results.fteRequired || '-'} FTE required` },
    { label: 'Cost Range',         value: `${fmt(results.costRange?.low, s)} – ${fmt(results.costRange?.high, s)}`, color: BLUE, sub: 'indicative range ±15%' },
    { label: 'Duration',           value: `${results.totalDurationWeeks} weeks`,  color: [249, 115, 22], sub: durationNote || `${Math.ceil(results.totalDurationWeeks / 4)} months` },
    { label: 'Audit Days (model)', value: `${results.auditDays || 'N/A'}`,        color: [245, 158, 11], sub: 'certification audit' },
  ];

  const mColW = (pageW - 46) / metrics.length;
  metrics.forEach((m, i) => {
    const x = 20 + i * (mColW + 2);
    doc.setFillColor(...LIGHT);
    doc.roundedRect(x, y, mColW, 24, 2, 2, 'F');
    doc.setFillColor(...m.color);
    doc.roundedRect(x, y, mColW, 2.5, 2, 2, 'F');

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.setTextColor(...GRAY);
    doc.text(m.label, x + 4, y + 9);

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9.5);
    doc.setTextColor(...DARK);
    doc.text(m.value, x + 4, y + 16);

    if (m.sub) {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(6);
      doc.setTextColor(...GRAY);
      doc.text(m.sub, x + 4, y + 21);
    }
  });
  y += 32;

  // ── Resource Allocation bar ───────────────────────────────────────────────
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(...DARK);
  doc.text('Resource Allocation', 20, y);
  y += 7;

  const totalDays = (results.internalDays || 0) + (results.consultantDays || 0);
  const intPct    = totalDays ? Math.round((results.internalDays / totalDays) * 100) : 50;
  const conPct    = 100 - intPct;
  const barW      = pageW - 40;

  doc.setFillColor(219, 234, 254);
  doc.roundedRect(20, y, barW, 8, 2, 2, 'F');
  doc.setFillColor(...BLUE);
  doc.roundedRect(20, y, barW * (intPct / 100), 8, 2, 2, 'F');
  if (conPct > 0) {
    doc.setFillColor(16, 185, 129);
    doc.roundedRect(20 + barW * (intPct / 100), y, barW * (conPct / 100), 8, 2, 2, 'F');
  }
  y += 13;

  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...BLUE);
  doc.text(`Internal: ${results.internalDays} days (${intPct}%)`, 20, y);
  doc.setTextColor(16, 185, 129);
  doc.text(`Consultant: ${results.consultantDays} days (${conPct}%)`, 20 + barW / 2, y);
  y += 10;

  // ── Cost Breakdown Table ───────────────────────────────────────────────────
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(...DARK);
  doc.text('Cost Breakdown', 20, y);
  y += 2;

  const bd    = results.costBreakdown || {};
  const total = Object.values(bd).reduce((a, b) => a + b, 0);

  autoTable(doc, {
    startY: y,
    margin: { left: 20, right: 20 },
    head: [['Category', 'Amount', '% of Total']],
    body: [
      ['Labor (Internal + Consultant)', fmt(bd.labor, s),    `${total ? Math.round((bd.labor    / total) * 100) : 0}%`],
      ['Certification Audit',           fmt(bd.audit, s),    `${total ? Math.round((bd.audit    / total) * 100) : 0}%`],
      ['Training & Awareness',          fmt(bd.training, s), `${total ? Math.round((bd.training / total) * 100) : 0}%`],
      ['Tools & Infrastructure',        fmt(bd.tools, s),    `${total ? Math.round((bd.tools    / total) * 100) : 0}%`],
    ],
    foot: [['Total', fmt(total, s), '100%']],
    theme: 'plain',
    styles:     { fontSize: 9, cellPadding: 3, textColor: DARK },
    headStyles: { fillColor: LIGHT, textColor: GRAY, fontStyle: 'bold', fontSize: 7 },
    footStyles: { fillColor: LIGHT, fontStyle: 'bold' },
    columnStyles: {
      1: { halign: 'right', fontStyle: 'bold' },
      2: { halign: 'right' },
    },
  });

  addFooter(doc, 1);

  // ════════════════════════════════════════════════════════════════════════════
  // PAGE 2 — Implementation Roadmap (Visual Gantt)
  // ════════════════════════════════════════════════════════════════════════════
  doc.addPage();
  y = 20;

  // Page 2 header strip
  doc.setFillColor(...BLUE);
  doc.rect(0, 0, pageW, 3, 'F');

  // Section title
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.setTextColor(...DARK);
  doc.text('Implementation Roadmap', 20, y + 8);

  const totalWks = results.totalDurationWeeks || 1;
  const totalMo  = Math.ceil(totalWks / 4);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(...GRAY);
  doc.text(
    `${totalMo}-month visual timeline  ·  ${totalWks} weeks total  ·  ${results.totalEffort} person-days`,
    20, y + 16
  );
  y += 26;

  // ── Visual Gantt chart ────────────────────────────────────────────────────
  if (results.phases && results.phases.length > 0) {
    const ganttEndY = drawGanttChart(
      doc,
      results.phases,
      totalWks,
      20,
      y,
      pageW - 40
    );
    y = ganttEndY + 8;

    // ── Phase colour legend ─────────────────────────────────────────────────
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.setTextColor(...GRAY);
    doc.text('PHASES', 20, y);
    y += 5;

    const legendCols = 3;
    const legendColW = (pageW - 40) / legendCols;
    results.phases.forEach((phase, idx) => {
      const col   = idx % legendCols;
      const row   = Math.floor(idx / legendCols);
      const lx    = 20 + col * legendColW;
      const ly    = y + row * 8;
      const [r, g, b] = PHASE_COLORS[idx % PHASE_COLORS.length];

      doc.setFillColor(r, g, b);
      doc.roundedRect(lx, ly - 3, 4, 4, 0.5, 0.5, 'F');
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7.5);
      doc.setTextColor(...DARK);
      doc.text(phase.name || phase.id, lx + 6, ly);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(6.5);
      doc.setTextColor(...GRAY);
      doc.text(`${phase.effort}d / ${phase.durationWeeks}w`, lx + 6, ly + 4.5);
    });

    const legendRows = Math.ceil(results.phases.length / legendCols);
    y += legendRows * 8 + 6;

    // ── Milestone summary row ─────────────────────────────────────────────
    const lastPhase       = results.phases[results.phases.length - 1];
    const internalAuditWk = Math.max(1, totalWks - (lastPhase?.durationWeeks || 0));

    const milestones = [
      { label: 'Project Start',        weekLabel: 'Week 1',                 color: BLUE },
      { label: 'Internal Audit',       weekLabel: `Week ${internalAuditWk}`, color: [245, 158, 11] },
      { label: 'Certification Target', weekLabel: `Week ${totalWks}`,        color: [16, 185, 129] },
    ];

    if (y + 22 < doc.internal.pageSize.getHeight() - 30) {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(8);
      doc.setTextColor(...GRAY);
      doc.text('KEY MILESTONES', 20, y);
      y += 5;

      const mStoneW = (pageW - 40) / 3;
      milestones.forEach((m, i) => {
        const mx = 20 + i * mStoneW;
        const [r, g, b] = m.color;

        doc.setFillColor(r, g, b, 0.08);
        doc.setFillColor(r + Math.round((255 - r) * 0.9), g + Math.round((255 - g) * 0.9), b + Math.round((255 - b) * 0.9));
        doc.roundedRect(mx, y, mStoneW - 4, 18, 2, 2, 'F');

        doc.setFillColor(r, g, b);
        doc.roundedRect(mx, y, mStoneW - 4, 2.5, 2, 2, 'F');

        doc.setFont('helvetica', 'bold');
        doc.setFontSize(7.5);
        doc.setTextColor(r, g, b);
        doc.text(m.label, mx + 4, y + 9);

        doc.setFont('helvetica', 'bold');
        doc.setFontSize(9);
        doc.setTextColor(...DARK);
        doc.text(m.weekLabel, mx + 4, y + 15);
      });
      y += 24;
    }

    // ── Duration factor callout ───────────────────────────────────────────
    if (durationFactors.length > 0 && y + 16 < doc.internal.pageSize.getHeight() - 30) {
      doc.setFillColor(255, 247, 237); // orange-50
      doc.roundedRect(20, y, pageW - 40, 14, 2, 2, 'F');
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(7.5);
      doc.setTextColor(249, 115, 22);
      doc.text('Timeline adjustments applied:', 25, y + 6);
      doc.setFont('helvetica', 'normal');
      doc.text(durationFactors.join('  ·  '), 25, y + 11);
      y += 20;
    }
  }

  addFooter(doc, 2);

  // ════════════════════════════════════════════════════════════════════════════
  // PAGE 3 — Key assumptions appendix (same canonical dataset as the app)
  // ════════════════════════════════════════════════════════════════════════════
  if (assumptionRows.length > 0) {
    doc.addPage();
    doc.setFillColor(...BLUE);
    doc.rect(0, 0, pageW, 3, 'F');

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(16);
    doc.setTextColor(...DARK);
    doc.text(t('assumptions.pdfTitle'), 20, 28);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(...GRAY);
    doc.text(doc.splitTextToSize(t('assumptions.intro'), pageW - 40), 20, 36);

    autoTable(doc, {
      startY: 48,
      margin: { left: 20, right: 20 },
      head: [[t('assumptions.title'), '', '']],
      body: assumptionRows.map(row => [row.label, row.value, t(PROVENANCE_KEYS[row.provenance] || row.provenance)]),
      theme: 'plain',
      styles:     { fontSize: 9, cellPadding: 3, textColor: DARK },
      headStyles: { fillColor: LIGHT, textColor: GRAY, fontStyle: 'bold', fontSize: 7 },
      columnStyles: {
        1: { halign: 'right', fontStyle: 'bold' },
        2: { halign: 'right', textColor: GRAY, fontSize: 7.5 },
      },
    });

    addFooter(doc, 3);
  }

  // ════════════════════════════════════════════════════════════════════════════
  // 3-Year Planning Outlook — kept clearly separate from the implementation
  // estimate, with recertification reported as excluded.
  // ════════════════════════════════════════════════════════════════════════════
  if (hasTco) {
    doc.addPage();
    doc.setFillColor(...BLUE);
    doc.rect(0, 0, pageW, 3, 'F');

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(16);
    doc.setTextColor(...DARK);
    doc.text(t('tco.title'), 20, 28);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(...GRAY);
    doc.text(doc.splitTextToSize(t('tco.note'), pageW - 40), 20, 36);

    let tableY = 50;
    tco.years.forEach(year => {
      autoTable(doc, {
        startY: tableY,
        margin: { left: 20, right: 20 },
        head: [[
          `${t('tco.yearLabel', { year: year.year })} — ${year.phase === 'implementation'
            ? t('tco.phaseImplementation') : t('tco.phaseOperation')}`,
          fmt(year.total, s),
        ]],
        body: year.components.map(component => [
          t(`tco.components.${component.id}`) + (component.days ? ` (${component.days}${t('tco.daysSuffix')})` : ''),
          fmt(component.amount, s),
        ]),
        theme: 'plain',
        styles:     { fontSize: 9, cellPadding: 2.5, textColor: DARK },
        headStyles: { fillColor: LIGHT, textColor: DARK, fontStyle: 'bold', fontSize: 8 },
        columnStyles: { 1: { halign: 'right', fontStyle: 'bold' } },
      });
      tableY = doc.lastAutoTable.finalY + 6;
    });

    autoTable(doc, {
      startY: tableY,
      margin: { left: 20, right: 20 },
      body: [
        [t('tco.year1Total'), fmt(tco.year1Total, s)],
        [t('tco.recurringTotal'), fmt(tco.recurringTotal, s)],
        [t('tco.total'), fmt(tco.total, s)],
      ],
      theme: 'plain',
      styles: { fontSize: 9.5, cellPadding: 3, textColor: DARK, fontStyle: 'bold' },
      columnStyles: { 1: { halign: 'right' } },
    });

    let noteY = doc.lastAutoTable.finalY + 8;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(...GRAY);
    [
      `${t('tco.recertTitle')}: ${t('tco.recertExcluded')}`,
      t('tco.surveillanceNote'),
      `${t('tco.modelVersion')}: ${tco.tcoModelVersion}`,
    ].forEach(line => {
      const lines = doc.splitTextToSize(line, pageW - 40);
      doc.text(lines, 20, noteY);
      noteY += lines.length * 4 + 3;
    });

    addFooter(doc, totalPages);
  }

  // Save file
  const filename = (name || 'estimate').replace(/[^a-zA-Z0-9]/g, '_').toLowerCase();
  doc.save(`isms_estimate_${filename}.pdf`);
}
